import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/hooks/usePermissions'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
    children: React.ReactNode
    requireAuth?: boolean
    requiredRole?: string | string[]
    /** Match on account role from user profile (not active role switcher). */
    requiredUserRole?: string | string[]
    /** Exact match on active role (no admin/super_admin blanket). Use for super_admin-only routes. */
    requiredExactRole?: string | string[]
    requiredPermission?: string
    redirectTo?: string
}

export function ProtectedRoute({
    children,
    requireAuth = true,
    requiredRole,
    requiredUserRole,
    requiredExactRole,
    requiredPermission,
    redirectTo = '/login'
}: ProtectedRouteProps) {
    const { isAuthenticated, isLoading: isAuthLoading, activeRole, user } = useAuth()
    const { hasPermission, hasRole, loading: isPermissionsLoading } = usePermissions()
    const location = useLocation()

    // Show loading spinner while checking authentication or permissions
    if (isAuthLoading || (requireAuth && isAuthenticated && isPermissionsLoading)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="text-muted-foreground font-medium">Loading...</span>
                </div>
            </div>
        )
    }

    // If route requires auth and user is not authenticated
    if (requireAuth && !isAuthenticated) {
        return <Navigate to={redirectTo} state={{ from: location }} replace />
    }

    if (requiredUserRole) {
        const accountRole = user?.role
        const allowedUserRoles = Array.isArray(requiredUserRole) ? requiredUserRole : [requiredUserRole]
        if (!accountRole || !allowedUserRoles.includes(accountRole)) {
            return <Navigate to="/dashboard" replace />
        }
    }

    // Strict role match (e.g. super_admin only — excludes admin)
    if (requiredExactRole) {
        const ar = activeRole || user?.role || null
        const allowed = Array.isArray(requiredExactRole) ? requiredExactRole : [requiredExactRole]
        if (!ar || !allowed.includes(ar)) {
            return <Navigate to="/dashboard" replace />
        }
    } else if (requiredRole && !hasRole(requiredRole)) {
        return <Navigate to="/dashboard" replace />
    }

    // If permission check is required
    if (requiredPermission && !hasPermission(requiredPermission)) {
        return <Navigate to="/dashboard" replace />
    }

    // If route is for non-authenticated users (like login) and user is authenticated
    if (!requireAuth && isAuthenticated) {
        const destination =
            typeof location.state?.from === 'string'
                ? location.state.from
                : location.state?.from?.pathname || '/dashboard'
        return <Navigate to={destination} replace />
    }

    return <>{children}</>
}
